/**
 * KpiTile — institutional KPI surface for intelligence / authority strips.
 *
 * Ivory→pearl gradient panel, hairline border, 30px tabular numeral,
 * tone-coloured sub-line dot. Used by /procurement (intelligence strip)
 * and /knowledge (authority strip). Hover lifts the tile when clickable.
 */
import type { CSSProperties } from 'react';

export type KpiTone = 'neutral' | 'sage' | 'wheat' | 'ember' | 'gold';

const TONE_ACCENT: Record<KpiTone, string> = {
  neutral: '#6B6558',
  sage: '#5F7A5C',
  wheat: '#B68A2E',
  ember: '#B8543A',
  gold: '#A98330',
};

const BASE_STYLE: CSSProperties = {
  background: 'linear-gradient(180deg, #FBFAF7 0%, #F4F2EC 100%)',
  border: '1px solid rgba(15, 23, 42, 0.06)',
  borderRadius: 14,
  padding: '18px 20px',
  boxShadow:
    '0 1px 0 rgba(255,255,255,0.8) inset, 0 12px 32px -20px rgba(15,23,42,0.18)',
  transition: 'transform 200ms ease, box-shadow 200ms ease',
};

const HOVER_SHADOW =
  '0 1px 0 rgba(255,255,255,0.8) inset, 0 16px 40px -18px rgba(15,23,42,0.24)';

export interface KpiTileProps {
  label: string;
  value: number;
  sub: string;
  tone: KpiTone;
  loading?: boolean;
  onClick?: () => void;
}

export function KpiTile({
  label,
  value,
  sub,
  tone,
  loading = false,
  onClick,
}: KpiTileProps) {
  const accent = TONE_ACCENT[tone];

  const inner = (
    <>
      <div
        className="text-[10.5px] font-medium uppercase tracking-[0.14em]"
        style={{ color: '#6B6558' }}
      >
        {label}
      </div>
      <div
        className="mt-2 text-[30px] font-medium leading-none tabular-nums"
        style={{ color: '#1A1A1A', letterSpacing: '-0.02em' }}
      >
        {loading ? '—' : value.toLocaleString()}
      </div>
      <div
        className="mt-2 flex items-center gap-1.5 text-[11px]"
        style={{ color: accent }}
      >
        <span
          aria-hidden
          className="inline-block h-1.5 w-1.5 rounded-full"
          style={{ backgroundColor: accent }}
        />
        {sub}
      </div>
    </>
  );

  if (onClick) {
    return (
      <button
        className="group text-left outline-none focus-visible:ring-2 focus-visible:ring-[#A98330] focus-visible:ring-offset-2"
        // A button centres its content once a grid stretches it; a tile
        // beside a two-line sub-line would sit lower than its neighbours.
        style={{
          ...BASE_STYLE,
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: 'flex-start',
        }}
        type="button"
        onClick={onClick}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = HOVER_SHADOW;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = BASE_STYLE.boxShadow as string;
        }}
      >
        {inner}
      </button>
    );
  }

  return <div style={BASE_STYLE}>{inner}</div>;
}
